// REQ-P1-06b: アーティスト初期設定判定向け補助API。

import { api, type paths } from './client';
import { ApiStatusError } from './templates';

type ArtistOnboardingStatusResponse = NonNullable<
  paths['/artist/onboarding/status']['get']['responses'][200]['content']
>['application/json'];
type ArtistOnboardingAnswerRequest = NonNullable<
  paths['/artist/onboarding/steps/{stepKey}/answer']['post']['requestBody']
>['content']['application/json'];
type ArtistProfileUpdateRequest = NonNullable<
  paths['/artists/profile']['put']['requestBody']
>['content']['application/json'];
type ArtistProfileUpdateResponse = NonNullable<
  paths['/artists/profile']['put']['responses'][200]['content']
>['application/json'];
type ArtistProfilePictureUploadRequest = NonNullable<
  paths['/artists/profile-picture']['put']['requestBody']
>['content']['multipart/form-data'];
type ArtistProfilePictureUploadResponse = NonNullable<
  paths['/artists/profile-picture']['put']['responses'][200]['content']
>['application/json'];
type ArtistProfilePictureDeleteResponse = NonNullable<
  paths['/artists/profile-picture']['delete']['responses'][200]['content']
>['application/json'];
type ArtistDeletionRequestResponse = NonNullable<
  paths['/artist/request-delete']['post']['responses'][200]['content']
>['application/json'];
type ArtistBankInfoResponse = NonNullable<
  paths['/artist/bank-info']['get']['responses'][200]['content']
>['application/json'];
type ArtistBankInfoUpdateRequest = NonNullable<
  paths['/artist/bank-info']['put']['requestBody']
>['content']['application/json'];
type ArtistBankInfoUpdateResponse = NonNullable<
  paths['/artist/bank-info']['put']['responses'][200]['content']
>['application/json'];
type ArtistChangePasswordRequest = NonNullable<
  paths['/artist/change-password']['put']['requestBody']
>['content']['application/json'];
type ArtistChangePasswordResponse = {
  success?: boolean;
  message?: string;
};
type ArtistEmailAvailabilityQuery = NonNullable<
  paths['/artist/email-available']['get']['parameters']['query']
>;
type ArtistEmailAvailabilityResponse = NonNullable<
  paths['/artist/email-available']['get']['responses'][200]['content']
>['application/json'];
type ArtistEmailChangeStatusResponse = NonNullable<
  paths['/artist/email-change/status']['get']['responses'][200]['content']
>['application/json'];
type ArtistEmailChangeRequest = NonNullable<
  paths['/artist/email-change/request']['post']['requestBody']
>['content']['application/json'];
type ArtistEmailChangeRequestResponse = NonNullable<
  paths['/artist/email-change/request']['post']['responses'][200]['content']
>['application/json'];
type ArtistEmailChangeConfirmRequest = NonNullable<
  paths['/artist/email-change/confirm']['post']['requestBody']
>['content']['application/json'];
type ArtistEmailChangeConfirmResponse = NonNullable<
  paths['/artist/email-change/confirm']['post']['responses'][200]['content']
>['application/json'];
type ArtistPhotosQuery = NonNullable<
  paths['/artist/photos']['get']['parameters']['query']
>;
type ArtistPhotosResponse = NonNullable<
  paths['/artist/photos']['get']['responses'][200]['content']
>['application/json'];
type ArtistPhotoUploadRequest = NonNullable<
  paths['/artist/photos']['post']['requestBody']
>['content']['multipart/form-data'];
type ArtistPhotoUploadResponse = NonNullable<
  paths['/artist/photos']['post']['responses'][200]['content']
>['application/json'];
type ArtistPhotoDeleteResponse = NonNullable<
  paths['/artist/photos/{id}']['delete']['responses'][200]['content']
>['application/json'];
type ArtistPhotoCommentUpdateRequest = NonNullable<
  paths['/artist/photos/{id}/comment']['put']['requestBody']
>['content']['application/json'];
type ArtistPhotoCommentUpdateResponse = NonNullable<
  paths['/artist/photos/{id}/comment']['put']['responses'][200]['content']
>['application/json'];
type ArtistNotificationPreferencesResponse = NonNullable<
  paths['/artist/notification-pref']['get']['responses'][200]['content']
>['application/json'];
type ArtistNotificationPreferencesUpdateRequest = NonNullable<
  paths['/artist/notification-pref']['post']['requestBody']
>['content']['application/json'];
type ArtistNotificationPreferencesUpdateResponse = NonNullable<
  paths['/artist/notification-pref']['post']['responses'][200]['content']
>['application/json'];
type ArtistLineLinkCodeResponse = NonNullable<
  paths['/line/link-code']['post']['responses'][200]['content']
>['application/json'];
type ArtistLineUnlinkResponse = NonNullable<
  paths['/line/unlink']['post']['responses'][200]['content']
>['application/json'];
type ArtistInviteValidityResponse = NonNullable<
  paths['/artist-invite/{token}']['get']['responses'][200]['content']
>['application/json'];
type ArtistRegistrationLinkRequest = NonNullable<
  paths['/artist-invite/{token}/request-link']['post']['requestBody']
>['content']['application/json'];
type ArtistRegistrationLinkResponse = NonNullable<
  paths['/artist-invite/{token}/request-link']['post']['responses'][200]['content']
>['application/json'];
type ArtistPasswordResetRequest = NonNullable<
  paths['/artist-password-reset/request']['post']['requestBody']
>['content']['application/json'];
type ArtistPasswordResetRequestResponse = NonNullable<
  paths['/artist-password-reset/request']['post']['responses'][200]['content']
>['application/json'];
type ArtistPasswordResetValidateResponse = NonNullable<
  paths['/artist-password-reset/{token}']['get']['responses'][200]['content']
>['application/json'];
type ArtistPasswordResetConfirmRequest = NonNullable<
  paths['/artist-password-reset/{token}']['post']['requestBody']
>['content']['application/json'];
type ArtistPasswordResetConfirmResponse = NonNullable<
  paths['/artist-password-reset/{token}']['post']['responses'][200]['content']
>['application/json'];
type ProvisionalArtistResponse = NonNullable<
  paths['/get-artist/{artistId}']['get']['responses'][200]['content']
>['application/json'];
type ArtistSignupFullRequest = NonNullable<
  paths['/setup-artist-full/{artistId}']['post']['requestBody']
>['content']['application/json'];
type ArtistSignupFullResponse = NonNullable<
  paths['/setup-artist-full/{artistId}']['post']['responses'][200]['content']
>['application/json'];
type ArtistGroupMembersResponse = NonNullable<
  paths['/artist/group-members']['get']['responses'][200]['content']
>['application/json'];
type ArtistGroupMemberAddRequest = NonNullable<
  paths['/artist/group-members']['post']['requestBody']
>['content']['application/json'];
type ArtistGroupMemberAddResponse = NonNullable<
  paths['/artist/group-members']['post']['responses'][200]['content']
>['application/json'];
type ArtistGroupMemberSearchQuery = NonNullable<
  paths['/artist/group-members/search']['get']['parameters']['query']
>;
type ArtistGroupMemberSearchResponse = NonNullable<
  paths['/artist/group-members/search']['get']['responses'][200]['content']
>['application/json'];
type ArtistGroupMemberRemoveResponse = NonNullable<
  paths['/artist/group-members/{artistId}']['delete']['responses'][200]['content']
>['application/json'];
type ArtistAccountTypeSwitchRequest = NonNullable<
  paths['/artist/account-type/switch']['post']['requestBody']
>['content']['application/json'];
type ArtistAccountTypeSwitchResponse = NonNullable<
  paths['/artist/account-type/switch']['post']['responses'][200]['content']
>['application/json'];

async function readJsonFromResponse<T>(data: T | undefined, response: Response): Promise<T> {
  if (data) return data;
  const payload = await response.clone().json().catch(() => undefined);
  if (payload) return payload as T;
  throw new ApiStatusError(response.status);
}

async function readApiErrorMessage(response: Response, fallback: string): Promise<string> {
  const payload = await response.clone().json().catch(() => ({}));
  if (payload && typeof payload === 'object') {
    const message = (payload as { message?: unknown; error?: unknown }).message
      || (payload as { message?: unknown; error?: unknown }).error;
    if (typeof message === 'string' && message.trim()) return message;
  }
  return fallback;
}

async function ensureOkWithMessage(response: Response, fallback: string): Promise<void> {
  if (response.ok) return;
  throw new Error(await readApiErrorMessage(response, fallback));
}

export async function getArtistOnboardingStatus(): Promise<ArtistOnboardingStatusResponse> {
  const { data, response } = await api.GET('/artist/onboarding/status');
  await ensureOkWithMessage(response, 'オンボーディング状況の取得に失敗しました');
  return readJsonFromResponse(data as ArtistOnboardingStatusResponse | undefined, response);
}

export async function answerArtistOnboardingStep(
  stepKey: string,
  answer: ArtistOnboardingAnswerRequest['answer']
): Promise<ArtistOnboardingStatusResponse> {
  const { data, response } = await api.POST('/artist/onboarding/steps/{stepKey}/answer', {
    params: { path: { stepKey } },
    body: { answer },
  });
  await ensureOkWithMessage(response, '回答の保存に失敗しました');
  return readJsonFromResponse(data as ArtistOnboardingStatusResponse | undefined, response);
}

export async function refreshArtistOnboardingStatus(): Promise<ArtistOnboardingStatusResponse> {
  const { data, response } = await api.POST('/artist/onboarding/refresh');
  await ensureOkWithMessage(response, 'オンボーディング状況の更新に失敗しました');
  return readJsonFromResponse(data as ArtistOnboardingStatusResponse | undefined, response);
}

export async function dismissArtistOnboarding(): Promise<ArtistOnboardingStatusResponse> {
  const { data, response } = await api.POST('/artist/onboarding/dismiss');
  await ensureOkWithMessage(response, 'オンボーディングのスキップ保存に失敗しました');
  return readJsonFromResponse(data as ArtistOnboardingStatusResponse | undefined, response);
}

export async function getArtistBankInfo(): Promise<ArtistBankInfoResponse | null> {
  const { data, response } = await api.GET('/artist/bank-info');
  if (!response.ok || !data) return null;
  return data as ArtistBankInfoResponse;
}

export async function updateArtistBankInfo(
  body: ArtistBankInfoUpdateRequest
): Promise<ArtistBankInfoUpdateResponse> {
  const { data, response } = await api.PUT('/artist/bank-info', { body });
  await ensureOkWithMessage(response, '保存に失敗しました');
  return readJsonFromResponse(data as ArtistBankInfoUpdateResponse | undefined, response);
}

export async function changeArtistPassword(
  body: ArtistChangePasswordRequest
): Promise<ArtistChangePasswordResponse> {
  const { data, response } = await api.PUT('/artist/change-password', { body });
  await ensureOkWithMessage(response, 'パスワードの変更に失敗しました');
  return readJsonFromResponse(data as ArtistChangePasswordResponse | undefined, response);
}

export async function checkArtistEmailAvailability(
  query: ArtistEmailAvailabilityQuery
): Promise<{
  ok: boolean;
  status: number;
  data: ArtistEmailAvailabilityResponse | undefined;
}> {
  const { data, response } = await api.GET('/artist/email-available', {
    params: { query },
  });
  return {
    ok: response.ok,
    status: response.status,
    data: data as ArtistEmailAvailabilityResponse | undefined,
  };
}

export async function getArtistEmailChangeStatus(): Promise<ArtistEmailChangeStatusResponse> {
  const { data, response } = await api.GET('/artist/email-change/status');
  await ensureOkWithMessage(response, 'メール情報の取得に失敗しました。');
  return readJsonFromResponse(data as ArtistEmailChangeStatusResponse | undefined, response);
}

export async function requestArtistEmailChange(
  body: ArtistEmailChangeRequest
): Promise<ArtistEmailChangeRequestResponse> {
  const { data, response } = await api.POST('/artist/email-change/request', { body });
  await ensureOkWithMessage(response, '送信に失敗しました。');
  return readJsonFromResponse(data as ArtistEmailChangeRequestResponse | undefined, response);
}

export async function confirmArtistEmailChange(body: ArtistEmailChangeConfirmRequest): Promise<{
  ok: boolean;
  status: number;
  data: (Partial<ArtistEmailChangeConfirmResponse> & {
    success?: boolean;
    status?: string;
    email?: string;
    message?: string;
  }) | undefined;
}> {
  const { data, response } = await api.POST('/artist/email-change/confirm', { body });
  const payload = data || await response.clone().json().catch(() => undefined);
  return {
    ok: response.ok,
    status: response.status,
    data: payload as (Partial<ArtistEmailChangeConfirmResponse> & {
      success?: boolean;
      status?: string;
      email?: string;
      message?: string;
    }) | undefined,
  };
}

export async function updateArtistProfile(
  body: ArtistProfileUpdateRequest
): Promise<ArtistProfileUpdateResponse> {
  const { data, response } = await api.PUT('/artists/profile', { body });
  await ensureOkWithMessage(response, '保存に失敗しました');
  return readJsonFromResponse(data as ArtistProfileUpdateResponse | undefined, response);
}

export async function uploadArtistProfilePicture(
  body: FormData
): Promise<ArtistProfilePictureUploadResponse> {
  const { data, response } = await api.PUT('/artists/profile-picture', {
    body: body as unknown as ArtistProfilePictureUploadRequest,
  });
  await ensureOkWithMessage(response, 'プロフィール画像の登録に失敗しました');
  return readJsonFromResponse(data as ArtistProfilePictureUploadResponse | undefined, response);
}

export async function deleteArtistProfilePicture(): Promise<ArtistProfilePictureDeleteResponse> {
  const { data, response } = await api.DELETE('/artists/profile-picture');
  await ensureOkWithMessage(response, 'プロフィール画像の削除に失敗しました');
  return readJsonFromResponse(data as ArtistProfilePictureDeleteResponse | undefined, response);
}

export async function requestArtistDeletion(): Promise<ArtistDeletionRequestResponse> {
  const { data, response } = await api.POST('/artist/request-delete');
  await ensureOkWithMessage(response, 'アカウント削除申請でエラーが発生しました。');
  return readJsonFromResponse(data as ArtistDeletionRequestResponse | undefined, response);
}

export async function listArtistGroupMembers(): Promise<ArtistGroupMembersResponse> {
  const { data, response } = await api.GET('/artist/group-members');
  await ensureOkWithMessage(response, '所属アーティストの取得に失敗しました');
  return readJsonFromResponse(data as ArtistGroupMembersResponse | undefined, response);
}

export async function searchArtistGroupMember(
  query: ArtistGroupMemberSearchQuery
): Promise<ArtistGroupMemberSearchResponse> {
  const { data, response } = await api.GET('/artist/group-members/search', {
    params: { query },
  });
  await ensureOkWithMessage(response, 'アーティストの検索に失敗しました');
  return readJsonFromResponse(data as ArtistGroupMemberSearchResponse | undefined, response);
}

export async function addArtistGroupMember(
  body: ArtistGroupMemberAddRequest
): Promise<ArtistGroupMemberAddResponse> {
  const { data, response } = await api.POST('/artist/group-members', { body });
  await ensureOkWithMessage(response, '所属アーティストの追加に失敗しました');
  return readJsonFromResponse(data as ArtistGroupMemberAddResponse | undefined, response);
}

export async function removeArtistGroupMember(
  artistId: string | number
): Promise<ArtistGroupMemberRemoveResponse> {
  const { data, response } = await api.DELETE('/artist/group-members/{artistId}', {
    params: { path: { artistId: String(artistId) } },
  });
  await ensureOkWithMessage(response, '所属アーティストの解除に失敗しました');
  return readJsonFromResponse(data as ArtistGroupMemberRemoveResponse | undefined, response);
}

export async function switchArtistAccountType(
  body: ArtistAccountTypeSwitchRequest
): Promise<ArtistAccountTypeSwitchResponse> {
  const { data, response } = await api.POST('/artist/account-type/switch', { body });
  await ensureOkWithMessage(response, 'アカウント種別の切り替えに失敗しました');
  return readJsonFromResponse(data as ArtistAccountTypeSwitchResponse | undefined, response);
}

export async function listArtistPhotos(
  query: ArtistPhotosQuery = {}
): Promise<ArtistPhotosResponse | null> {
  const { data, response } = await api.GET('/artist/photos', {
    params: { query },
  });
  if (!response.ok || !data) return null;
  return data as ArtistPhotosResponse;
}

export async function requireArtistPhotos(
  query: ArtistPhotosQuery = {},
  fallbackMessage = '宣材写真の取得に失敗しました'
): Promise<ArtistPhotosResponse> {
  const { data, response } = await api.GET('/artist/photos', {
    params: { query },
  });
  await ensureOkWithMessage(response, fallbackMessage);
  return readJsonFromResponse(data as ArtistPhotosResponse | undefined, response);
}

export async function uploadArtistPhotos(
  body: FormData,
  fallbackMessage = '宣材写真の登録に失敗しました'
): Promise<ArtistPhotoUploadResponse> {
  const { data, response } = await api.POST('/artist/photos', {
    body: body as unknown as ArtistPhotoUploadRequest,
  });
  if (!response.ok) {
    const payload = await response.clone().json().catch(() => ({})) as {
      code?: string;
      message?: string;
      similar?: unknown[];
    };
    const error = new Error(payload.message || fallbackMessage) as Error & {
      status?: number;
      code?: string;
      similar?: unknown[];
    };
    error.status = response.status;
    error.code = payload.code;
    error.similar = payload.similar;
    throw error;
  }
  return readJsonFromResponse(data as ArtistPhotoUploadResponse | undefined, response);
}

export async function deleteArtistPhoto(
  id: number | string,
  fallbackMessage = '写真の削除に失敗しました'
): Promise<ArtistPhotoDeleteResponse> {
  const { data, response } = await api.DELETE('/artist/photos/{id}', {
    params: { path: { id } },
  });
  await ensureOkWithMessage(response, fallbackMessage);
  return readJsonFromResponse(data as ArtistPhotoDeleteResponse | undefined, response);
}

export async function updateArtistPhotoComment(
  id: number | string,
  body: ArtistPhotoCommentUpdateRequest,
  fallbackMessage = 'コメントの保存に失敗しました'
): Promise<ArtistPhotoCommentUpdateResponse> {
  const { data, response } = await api.PUT('/artist/photos/{id}/comment', {
    params: { path: { id } },
    body,
  });
  await ensureOkWithMessage(response, fallbackMessage);
  return readJsonFromResponse(data as ArtistPhotoCommentUpdateResponse | undefined, response);
}

export async function getArtistNotificationPreferences(): Promise<
  ArtistNotificationPreferencesResponse | null
> {
  const { data, response } = await api.GET('/artist/notification-pref');
  if (!response.ok || !data) return null;
  return data as ArtistNotificationPreferencesResponse;
}

export async function requireArtistNotificationPreferences(): Promise<
  ArtistNotificationPreferencesResponse
> {
  const { data, response } = await api.GET('/artist/notification-pref');
  await ensureOkWithMessage(response, '通知設定の取得に失敗しました。');
  return readJsonFromResponse(data as ArtistNotificationPreferencesResponse | undefined, response);
}

export async function updateArtistNotificationPreferences(
  body: ArtistNotificationPreferencesUpdateRequest
): Promise<ArtistNotificationPreferencesUpdateResponse> {
  const { data, response } = await api.POST('/artist/notification-pref', { body });
  await ensureOkWithMessage(response, '通知設定の更新に失敗しました。');
  return readJsonFromResponse(
    data as ArtistNotificationPreferencesUpdateResponse | undefined,
    response
  );
}

export async function issueArtistLineLinkCode(): Promise<{
  ok: boolean;
  status: number;
  data: ArtistLineLinkCodeResponse | undefined;
}> {
  const { data, response } = await api.POST('/line/link-code');
  return {
    ok: response.ok,
    status: response.status,
    data: data as ArtistLineLinkCodeResponse | undefined,
  };
}

export async function unlinkArtistLine(): Promise<ArtistLineUnlinkResponse> {
  const { data, response } = await api.POST('/line/unlink');
  await ensureOkWithMessage(response, '連携解除に失敗しました。');
  return readJsonFromResponse(data as ArtistLineUnlinkResponse | undefined, response);
}

export async function getArtistInviteValidity(token: string): Promise<{
  ok: boolean;
  status: number;
  data: ArtistInviteValidityResponse | undefined;
}> {
  const { data, response } = await api.GET('/artist-invite/{token}', {
    params: { path: { token } },
  });
  return {
    ok: response.ok,
    status: response.status,
    data: data as ArtistInviteValidityResponse | undefined,
  };
}

export async function requestArtistRegistrationLink(
  token: string,
  body: ArtistRegistrationLinkRequest
): Promise<{
  ok: boolean;
  status: number;
  data: ArtistRegistrationLinkResponse | undefined;
  message?: string;
}> {
  const { data, response } = await api.POST('/artist-invite/{token}/request-link', {
    params: { path: { token } },
    body,
  });
  return {
    ok: response.ok,
    status: response.status,
    data: data as ArtistRegistrationLinkResponse | undefined,
    message: response.ok ? undefined : await readApiErrorMessage(response, '送信に失敗しました。'),
  };
}

export async function requestArtistPasswordReset(
  body: ArtistPasswordResetRequest
): Promise<ArtistPasswordResetRequestResponse | undefined> {
  const { data } = await api.POST('/artist-password-reset/request', { body });
  return data as ArtistPasswordResetRequestResponse | undefined;
}

export async function validateArtistPasswordResetToken(token: string): Promise<{
  ok: boolean;
  status: number;
  data: ArtistPasswordResetValidateResponse | undefined;
  reason?: string;
}> {
  const { data, response } = await api.GET('/artist-password-reset/{token}', {
    params: { path: { token } },
  });
  const payload = response.ok ? undefined : await response.clone().json().catch(() => ({}));
  return {
    ok: response.ok,
    status: response.status,
    data: data as ArtistPasswordResetValidateResponse | undefined,
    reason: typeof payload?.reason === 'string' ? payload.reason : undefined,
  };
}

export async function confirmArtistPasswordReset(
  token: string,
  body: ArtistPasswordResetConfirmRequest
): Promise<{
  ok: boolean;
  status: number;
  data: ArtistPasswordResetConfirmResponse | undefined;
  message?: string;
}> {
  const { data, response } = await api.POST('/artist-password-reset/{token}', {
    params: { path: { token } },
    body,
  });
  return {
    ok: response.ok,
    status: response.status,
    data: data as ArtistPasswordResetConfirmResponse | undefined,
    message: response.ok
      ? undefined
      : await readApiErrorMessage(response, 'パスワードの再設定に失敗しました。'),
  };
}

export async function getProvisionalArtistForSignup(artistId: string): Promise<{
  ok: boolean;
  status: number;
  data: ProvisionalArtistResponse | undefined;
  message?: string;
}> {
  const { data, response } = await api.GET('/get-artist/{artistId}', {
    params: { path: { artistId } },
  });
  return {
    ok: response.ok,
    status: response.status,
    data: data as ProvisionalArtistResponse | undefined,
    message: response.ok
      ? undefined
      : await readApiErrorMessage(response, '登録ページの読み込みに失敗しました。'),
  };
}

export async function setupArtistFullFromInvite(
  artistId: string,
  body: ArtistSignupFullRequest
): Promise<{
  ok: boolean;
  status: number;
  data: ArtistSignupFullResponse | undefined;
  message?: string;
}> {
  const { data, response } = await api.POST('/setup-artist-full/{artistId}', {
    params: { path: { artistId } },
    body,
  });
  return {
    ok: response.ok,
    status: response.status,
    data: data as ArtistSignupFullResponse | undefined,
    message: response.ok ? undefined : await readApiErrorMessage(response, '登録に失敗しました。'),
  };
}

export async function getArtistDeletionPolicy(): Promise<{ deleteDelayDays: number }> {
  const { data, response } = await api.GET('/artist/deletion-policy');
  if (!response.ok || !data || !(data.deleteDelayDays >= 1)) throw new Error('猶予期間を確認できませんでした。');
  return data;
}
